/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, Typography } from '@mui/material';
import type { SxProps } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@opengeoweb/theme';
import { SHARED_NAMESPACE } from '../../utils/i18n';
import { CustomIconButton } from '../CustomIconButton';
import { CustomTooltip } from '../CustomTooltip';
import type { HeaderSize, ToolHeaderProps } from './types';

const headerStyle: SxProps = {
  containerType: 'inline-size',
  containerName: 'tool-header',
  '.header-title': {
    display: 'none',
  },
  '@container tool-header (min-width: 321px)': {
    '.header-title': {
      display: 'block',
    },
  },
};

const getStyle = (size: HeaderSize): React.CSSProperties => {
  switch (size) {
    case 'xs': {
      return {
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '22px',
        marginTop: '-1px',
      };
    }
    case 'small': {
      return {
        fontSize: 16,
        fontWeight: 500,
        padding: '4px',
      };
    }
    case 'medium': {
      return {
        fontSize: 20,
        fontWeight: 400,
        padding: '4px 8px',
        height: 40,
      };
    }
    case 'large': {
      return {
        fontSize: 16,
        fontWeight: 500,
        padding: '12px',
      };
    }
    default: {
      return {};
    }
  }
};

const iconStyle: SxProps = {
  color: 'geowebColors.greys.accessible',
  padding: 0,
  '&:hover': {
    background: 'action.hover',
  },
};

const ToolHeader: React.FC<ToolHeaderProps> = ({
  size,
  title,
  onClose = null!,
  isDraggable = false,
  className = '',
  leftHeaderComponent = null,
  rightHeaderComponent = null,
  closeIcon = <Close data-testid="CloseIcon" />,
}: ToolHeaderProps) => {
  const styles = getStyle(size!);

  const columnSizeIcon =
    title && (leftHeaderComponent ?? rightHeaderComponent) ? 4 : 1;
  const columnSizeTitle =
    (leftHeaderComponent ?? rightHeaderComponent) ? 4 : 10;
  const columnSizeLeft = title ? columnSizeIcon : 12 - columnSizeIcon;
  const { t } = useTranslation(SHARED_NAMESPACE);

  return (
    <Box
      aria-label="toolheader"
      className={className}
      component="header"
      role="heading"
      sx={{
        ...(rightHeaderComponent ? headerStyle : {}),
        boxShadow: 1, // elevation_01
        backgroundColor: 'geowebColors.background.surface',
        zIndex: 2,
        cursor: isDraggable ? 'move' : null,
      }}
    >
      <Grid
        alignItems="center"
        container
        justifyContent="space-between"
        sx={{
          padding: styles.padding,
          height: styles.height,
        }}
      >
        <Grid
          className="header-left"
          size={columnSizeLeft}
          sx={{ paddingRight: 1 }}
        >
          {leftHeaderComponent ?? null}
        </Grid>
        <Grid
          alignItems="center"
          className="header-title"
          container
          justifyContent="center"
          size={title ? columnSizeTitle : 0}
          sx={{ minHeight: 12 }}
        >
          {title ? (
            <Typography
              noWrap
              role="heading"
              sx={{
                fontSize: styles.fontSize,
                fontWeight: styles.fontWeight,
                lineHeight: styles.lineHeight,
                marginTop: styles.marginTop,
                textAlign: 'center',
              }}
              variant="h2"
            >
              {title}
            </Typography>
          ) : null}
        </Grid>
        <Grid
          className="header-right"
          container
          justifyContent="flex-end"
          size={columnSizeIcon}
        >
          {rightHeaderComponent ?? null}
          {onClose ? (
            <CustomTooltip
              placement="bottom"
              sx={{ pointerEvents: 'none' }}
              title={t('shared-close')}
            >
              <CustomIconButton
                data-testid="closeBtn"
                onClick={onClose}
                onTouchEnd={onClose}
                sx={{
                  '&.MuiButtonBase-root.MuiIconButton-root': iconStyle,
                }}
              >
                {closeIcon}
              </CustomIconButton>
            </CustomTooltip>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ToolHeader;
